import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Types
import Reducers from "app/types/Reducers";
// Models
import { IConversation, IConversationQuestion, IMessage } from "app/models/ChatAI";
// Async
import {
  getConversations, createConversation, updateConversation, deleteConversation, createQuestion,
  refineAnswer
} from './AIChat.async';

interface IState {
  conversations: IConversation[] | null;
  conversation: IConversation | null;
  // On details page we want to skip initial fetch
  // because we already have conversation in the store when crate new one
  skipInitialChatFetch: boolean;
  lastAnimatedMessageId: string | null;
  selectedPromptContent: string | null;


  // ToDO: Should be removed
  loading: boolean;
  loadingMessage: boolean;
  statusMessage: 'initial' | 'error' | 'sent';
  refineParentId: string | null;
}

const initialState:IState = {
  conversations: null,
  conversation: null,
  skipInitialChatFetch: false,
  lastAnimatedMessageId: null,
  selectedPromptContent: null,


  loading: false,
  loadingMessage: false,
  statusMessage: 'initial',
  refineParentId: null
};

const slice = createSlice({
  name: Reducers.AIChat,
  initialState,
  reducers: {
    addRefineMessage: (state:IState, action:PayloadAction<{
      conversationId:string;
      idempotencyKey:string;
      data:any
    }>) => {
      const { conversationId, idempotencyKey, data } = action.payload;
      if ( state.conversation && state.conversation.id === conversationId ){
        const message = generateAIMessage(conversationId, idempotencyKey, data);
        state.conversation = {
          ...state.conversation,
          messages: updateMessages(state.conversation.messages || [], message),
          status: 'idle'
        };
        state.lastAnimatedMessageId = message.id;
        // state.loadingMessage = false;
        // state.statusMessage = 'sent';
        // state.refineParentId = null;
      }
      if ( state.conversations ){
        state.conversations = state.conversations.map((conversation:IConversation) => {
          if ( conversation.id === conversationId ){
            return {
              ...conversation,
              status: 'idle'
            };
          }
          return conversation;
        });
      }
    },
    addErrorMessageFromAI: (state:IState, action:PayloadAction<IMessage>) => {
      if ( state.conversation ){
        const reversedMessages = [...(state.conversation.messages || [])].reverse();
        const updatedMessages = reversedMessages.map((message, index) => {
          if ( message.type === 'human' && index === 0 ){
            return { ...message, error: true };
          }
          return message;
        }).reverse();
        state.conversation = {
          ...state.conversation,
          messages: [...updatedMessages, action.payload]
        }
      }
    },
    setLastAnimatedMessageId: (state:IState, action:PayloadAction<string | null>) => {
      state.lastAnimatedMessageId = action.payload;
    },
    setSelectedPromptContent: (state:IState, action:PayloadAction<string | null>) => {
      state.selectedPromptContent = action.payload;
    },
    setConversation: (state:IState, action:PayloadAction<{
      conversation: IConversation;
      question?:IConversationQuestion,
      enableConversationUpdate?:boolean,
      enableLastAnimatedMessageId?:boolean
    }>) => {
      const { conversation, question, enableConversationUpdate = true, enableLastAnimatedMessageId = false } = action.payload;
      const messages = conversation.messages || [];
      if ( enableLastAnimatedMessageId ){
        // Set lastAnimatedMessageId if last message is AI
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
        if ( lastMessage && lastMessage.id && lastMessage.type === 'ai' ) {
          state.lastAnimatedMessageId = lastMessage.id;
        }
      }
      const updatedMessages = question
        ? [...messages, generateHumanMessage(conversation.id, question)]
        : messages
      ;
      if ( enableConversationUpdate ){
        state.conversation = {
          ...conversation,
          messages: updatedMessages
        };
      }
      if ( state.conversations ){
        state.conversations = state.conversations.map((c:IConversation) => {
          if ( c.id === conversation.id ){
            return {
              ...c,
              ...conversation,
              messages: updatedMessages
            };
          }
          return c;
        });
      }
      // state.loadingMessage = true;
    },
    // setStatusMessage: (state:IState, action: PayloadAction<'initial' | 'error' | 'sent'>) => {
    //   state.statusMessage = action.payload;
    // },
    setSkipInitialChatFetch: (state:IState, action:PayloadAction<boolean>) => {
      state.skipInitialChatFetch = action.payload;
    },
    // Default
    setInitialField: <IStateKey extends keyof IState>(state: IState, action: PayloadAction<IStateKey>) => {
      state[action.payload] = initialState[action.payload];
    },
    resetState: () => initialState
  },
  extraReducers(builder){
    // Get conversations
    builder.addCase(getConversations.pending, (state) => {
      state.conversations = null;
    });
    builder.addCase(getConversations.fulfilled, (state, action:PayloadAction<IConversation[]>) => {
      state.conversations = action.payload;
    });
    builder.addCase(getConversations.rejected, (state) => {
      state.conversations = [];
    });
    // // Get conversation
    // builder.addCase(getConversation.fulfilled, (state, action:PayloadAction<IConversation>) => {
    //   state.conversation = action.payload;
    // });
    // Create conversation
    builder.addCase(createConversation.fulfilled, (state, action:PayloadAction<IConversation>) => {
      if ( state.conversations ){
        state.conversations = [action.payload, ...state.conversations]
      };
    });
    // Update conversation
    builder.addCase(updateConversation.fulfilled, (state, action:PayloadAction<IConversation>) => {
      if ( state.conversation && state.conversation.id === action.payload.id ){
        state.conversation = {...state.conversation, ...action.payload};
      }
      if ( state.conversations ){
        state.conversations = state.conversations.map((conversation:IConversation) => {
          if ( conversation.id === action.payload.id ) return action.payload;
          return conversation;
        });
      }
    });
    // Delete conversation
    builder.addCase(deleteConversation.fulfilled, (state, action:PayloadAction<string>) => {
      if ( state.conversations ){
        state.conversations = state.conversations.filter((conversation:IConversation) => conversation.id !== action.payload)
      }
    });
    // Create question
    builder.addCase(createQuestion.pending, (state, action:any) => {
      const { conversationId, data } = action.meta.arg;
      if ( state.conversation ){
        const messages = state.conversation.messages || [];
        const humanMessage = generateHumanMessage(conversationId, data);
        state.conversation = {
          ...state.conversation,
          messages: [...messages, humanMessage],
          status: 'answering'
        };
      }
      if ( state.conversations ){
        state.conversations = state.conversations.map((conversation:IConversation) => {
          if ( conversation.id === conversationId ){
            return {
              ...conversation,
              status: 'answering'
            };
          }
          return conversation;
        });
      }
      // state.loadingMessage = true;
    });
    // Refine answer
    builder.addCase(refineAnswer.pending, (state, action:any) => {
      const { conversationId, data } = action.meta.arg;
      if (state.conversation) {
        const messages = state.conversation.messages || [];
        const lastHumanMessage = [...messages].reverse().find(msg => msg.type === 'human');
        if ( lastHumanMessage ) {
          const updatedMessages = messages.map((message: IMessage) => {
            if ( message.id !== lastHumanMessage.id ) return message;
            return {
              ...message,
              metadata: {
                ...(message.metadata || {}),
                parameters: {
                  ...message.metadata?.parameters,
                  coverage: data.coverage
                }
              }
            };
          });
          state.conversation.messages = updatedMessages;
        }
        state.conversation.status = 'answering';
      }
      if ( state.conversations ){
        state.conversations = state.conversations.map((conversation:IConversation) => {
          if ( conversation.id === conversationId ){
            return {
              ...conversation,
              status: 'answering'
            };
          }
          return conversation;
        });
      }
    });
    // // Matcher
    // builder.addMatcher(
    //   (action:AnyAction) => action.type.includes('fulfilled') || action.type.includes('rejected'),
    //   (state, action:PayloadAction<any>) => {
    //     const { payload, type } = action;
    //     state.loading = false;
    //     if ( type.includes('rejected') && payload && payload.statusCode === 500 ){
    //       state.loadingMessage = false;
    //       state.refineParentId = null;
    //     }
    //   }
    // );
  }
});

export const AIChatActions = slice.actions;

export default slice.reducer;

// When a message is received from a push notification or from a response
// we need to pass `parentid` if it is the first message from `human`
// and delete a duplicate message using `idempotencyKey`
const updateMessages = (messages:IMessage[], message:IMessage):IMessage[] => {
  const clonedMessages = [...messages]; // avoid mutation
  const updatedMessages = assignParentIdToFirstHumanMessage(clonedMessages, message);
  const foundMessage = findMessageByIdempotencyKey(updatedMessages, message.idempotencyKey as string);
  return foundMessage
    ? updatedMessages.map((m:IMessage) => {
        if ( !m.idempotencyKey ) return m;
        return m.idempotencyKey === message.idempotencyKey ? { ...m, ...message } : m;
      })
    : avoidMessageDuplicationOnRefine(updatedMessages, message)
};

const assignParentIdToFirstHumanMessage = (messages:IMessage[], message:IMessage):IMessage[] => {
  // First question doesn`t have `id`
  // So in this case we will set `id` from first answer from `parentId`
  for ( let i = messages.length - 1; i >= 0; i-- ){
    if ( !messages[i].id && messages[i].type === 'human' ){
      messages[i] = {
        ...messages[i],
        id: message.parentId
      };
      break;
    }
  }
  return messages;
};

const findMessageByIdempotencyKey = (messages:IMessage[], idempotencyKey:string):IMessage | undefined => {
  return messages ? messages.find((message:IMessage) => message.idempotencyKey === idempotencyKey) : undefined;
};

// On refine we might get `metadata` with `maxTokenReach`
// Which will mean, that last response will return last message with updated `matadata` attribute
const avoidMessageDuplicationOnRefine = (messages:IMessage[], message:IMessage):IMessage[] => {
  const foundMessage = messages.find((m:IMessage) => m.id === message.id);
  if ( !foundMessage ) return [...messages, message];
  return messages.map((msg:IMessage) => {
    if ( msg.id === message.id ) return message;
    return msg;
  });
};

const generateHumanMessage = (conversationId: string, question: IConversationQuestion): IMessage => ({
  createdOn: new Date().toString(),
  conversationId,
  text: question.text,
  type: 'human',
  ...(question.coverage !== undefined && {
    metadata: {
      parameters: {
        coverage: question.coverage
      }
    }
  })
});

const generateAIMessage = (conversationId:string, idempotencyKey:string, data:any) => ({
  ...data,
  createdOn: new Date().toString(),
  conversationId,
  idempotencyKey,
  type: 'ai'
})
